import { useState, useEffect, useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { fetchLetters } from "./lettersApi";

const DEFAULTS = {
  project_details: "", // the overarching project e.g. "Blackspot Porbandar"
  project: "", // the section within it
  status: "", // open | closed | "" (both)
  date_from: "",
  date_to: "",
  search: "",
  ordering: "-date",
  page: "1",
};

/**
 * Reads filters from the URL query string (so the register is shareable/bookmarkable),
 * fetches results whenever they change, and exposes setters that update the URL.
 */
export function useLettersQuery() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState({ count: 0, results: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const filters = useMemo(() => {
    const out = { ...DEFAULTS };
    for (const key of Object.keys(DEFAULTS)) {
      if (searchParams.has(key)) out[key] = searchParams.get(key);
    }
    return out;
  }, [searchParams]);

  const setFilter = useCallback(
    (key, value) => {
      const next = new URLSearchParams(searchParams);
      if (value) next.set(key, value);
      else next.delete(key);
      // any real filter change resets pagination
      if (key !== "page") next.set("page", "1");
      setSearchParams(next);
    },
    [searchParams, setSearchParams]
  );

  // For changing multiple filters at once (e.g. project changes AND section resets together) —
  // calling setFilter twice in a row is a race: both reads happen from the same stale
  // searchParams snapshot before React re-renders, so the second call can silently undo the first.
  const setFilters = useCallback(
    (updates) => {
      const next = new URLSearchParams(searchParams);
      Object.entries(updates).forEach(([key, value]) => {
        if (value) next.set(key, value);
        else next.delete(key);
      });
      next.set("page", "1");
      setSearchParams(next);
    },
    [searchParams, setSearchParams]
  );

  const setSort = useCallback(
    (field) => {
      const current = filters.ordering;
      let next;
      if (current === field) next = `-${field}`;
      else if (current === `-${field}`) next = field;
      else next = field;
      setFilter("ordering", next);
    },
    [filters.ordering, setFilter]
  );

  const clearFilters = useCallback(() => {
    setSearchParams({});
  }, [setSearchParams]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    fetchLetters(filters)
      .then((res) => {
        if (!cancelled) setData(res);
      })
      .catch((err) => {
        if (!cancelled) setError(err);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(filters)]);

  return { filters, setFilter, setFilters, setSort, clearFilters, data, loading, error };
}